import {
    createEditor,
    Element as SlateElement,
    Node,
    Text,
    Transforms,
    Descendant,
} from "slate";
import {
    ListItemElement,
    ListType,
    ParagraphElement,
    SlateValue,
    createEmptySlateValue,
} from "./slateTypes";

export const isChecklistItem = (
    element: ListItemElement,
): element is ListItemElement & { checked: boolean } =>
    element.checked === true || element.checked === false;

export const toggleChecklistItemAtPath = (
    value: SlateValue,
    paths: number[],
): SlateValue => {
    const editor = createEditor();
    editor.children = value;
    if (!Node.has(editor, paths)) {
        return value;
    }
    const node = Node.get(editor, paths);
    if (
        !SlateElement.isElement(node) ||
        node.type !== "list-item" ||
        !isChecklistItem(node)
    ) {
        return value;
    }
    Transforms.setNodes(editor, { checked: !node.checked }, { at: paths });
    return editor.children;
};

export const serializeSlateValue = (value: SlateValue): string =>
    JSON.stringify(value);

export const deserializeSlateValue = (content: string): SlateValue => {
    try {
        const parsed: unknown = JSON.parse(content);
        if (Array.isArray(parsed)) {
            return parsed;
        }
        return createEmptySlateValue();
    } catch {
        return createEmptySlateValue();
    }
};

export const hasSlateContent = (nodes: Descendant[]): boolean => {
    const stack: Descendant[] = [...nodes];

    while (stack.length > 0) {
        const node = stack.pop();
        if (!node) {
            continue;
        }

        if (Text.isText(node)) {
            if (node.text.trim().length > 0) {
                return true;
            }
            continue;
        }

        if (SlateElement.isElement(node)) {
            if (node.type === "mention" || node.type === "entityRef") {
                return true;
            }
            stack.push(...node.children);
        }
    }

    return false;
};

export const isSlateValueEmpty = (value: SlateValue): boolean =>
    !hasSlateContent(value);

const inlineToPlainText = (node: Descendant): string =>
    SlateElement.isElement(node) && node.type === "entityRef"
        ? node.label
        : Node.string(node);

const paragraphToPlainText = (paragraph: ParagraphElement): string =>
    paragraph.children.map(inlineToPlainText).join("");

const nodeToPlainText = (node: Descendant): string => {
    if (!SlateElement.isElement(node)) {
        return Node.string(node);
    }
    if (node.type === "bulleted-list" || node.type === "numbered-list") {
        return listToPlainText(node, 0);
    }
    if (node.type === "paragraph") {
        return paragraphToPlainText(node);
    }
    return Node.string(node);
};

const listItemMarker = ({
    listType,
    item,
    index,
}: {
    listType: ListType;
    item: ListItemElement;
    index: number;
}): string => {
    if (listType === "numbered-list") {
        return `${index + 1}. `;
    }
    if (isChecklistItem(item)) {
        return item.checked ? "- [x] " : "- [ ] ";
    }
    return "- ";
};

const listToPlainText = (
    list: Extract<Descendant, { type: "bulleted-list" | "numbered-list" }>,
    indent: number,
): string => {
    if (!("children" in list)) {
        return "";
    }
    return list.children
        .map((item, index) => {
            const marker = listItemMarker({ listType: list.type, item, index });
            const indentPrefix = "  ".repeat(indent);
            const lines: string[] = [];
            for (const child of item.children) {
                if (!SlateElement.isElement(child)) {
                    continue;
                }
                if (child.type === "paragraph") {
                    lines.push(
                        `${indentPrefix}${marker}${paragraphToPlainText(child)}`,
                    );
                    continue;
                }
                if (
                    child.type === "bulleted-list" ||
                    child.type === "numbered-list"
                ) {
                    lines.push(listToPlainText(child, indent + 1));
                }
            }
            return lines.join("\n");
        })
        .join("\n");
};

export const slateToPlainText = (content: string): string => {
    const nodes = deserializeSlateValue(content);
    return nodes.map(nodeToPlainText).join("\n").trim();
};

export const isUrl = (text: string): boolean => {
    try {
        const url = new URL(text);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
};

export const getFullName = (
    user:
        | {
              preferredName?: string | null;
              name?: string | null;
              firstName?: string | null;
          }
        | undefined
        | null,
    fallback: string,
): string => user?.preferredName ?? user?.name ?? user?.firstName ?? fallback;
